import React, { useEffect, useState, useContext } from "react";
import api, { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Plus, Trash2 } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { DrillCtx } from "@/components/reports/DrillDownDrawer";

const downloadXlsx = (path, filename) => downloadBlob(path, filename);

const RATIO_LABELS = {
  current_ratio: "Current Ratio",
  debt_equity_ratio: "Debt-Equity Ratio",
  debt_service_coverage: "Debt Service Coverage",
  return_on_equity: "Return on Equity",
  inventory_turnover: "Inventory Turnover",
  trade_receivables_turnover: "Trade Receivables Turnover",
  trade_payables_turnover: "Trade Payables Turnover",
  net_capital_turnover: "Net Capital Turnover",
  net_profit_ratio: "Net Profit Ratio",
  return_on_capital_employed: "Return on Capital Employed",
  return_on_investment: "Return on Investment",
};

const AGE_LABELS = { "0_6m": "< 6 months", "6m_1y": "6m – 1y", "1_2y": "1 – 2 y", "2_3y": "2 – 3 y", gt_3y: "> 3 y" };

function CLDisclosure() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ nature: "", amount: "", remarks: "" });
  const load = async () => setRows((await api.get("/reports/schedule3/contingent-liabilities")).data || []);
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.nature || !form.amount) return toast.error("Nature and amount required");
    await api.post("/reports/schedule3/contingent-liabilities", { ...form, amount: Number(form.amount) });
    setForm({ nature: "", amount: "", remarks: "" }); load(); toast.success("Added");
  };
  const del = async (id) => { await api.delete(`/reports/schedule3/contingent-liabilities/${id}`); load(); };
  return (
    <div className="card-sharp p-5" data-testid="cl-disclosure">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Contingent Liabilities (not provided for)</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <Input placeholder="Nature of liability" value={form.nature} onChange={(e) => setForm({ ...form, nature: e.target.value })} data-testid="cl-nature" />
        <Input placeholder="Amount ₹" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cl-amount" />
        <Input placeholder="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="cl-remarks" />
        <Button onClick={add} data-testid="cl-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b"><th className="text-left py-2">Nature</th><th className="text-right">Amount</th><th className="text-left">Remarks</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="text-muted-foreground text-center py-3">No entries yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b">
              <td className="py-2">{r.nature}</td>
              <td className="text-right">{fmtINR(r.amount)}</td>
              <td>{r.remarks}</td>
              <td className="text-right"><Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditorDisclosure() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ fy: "25-26", head: "", amount: "" });
  const load = async () => setRows((await api.get("/reports/schedule3/auditor-remuneration")).data || []);
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.head || !form.amount) return toast.error("Head and amount required");
    await api.post("/reports/schedule3/auditor-remuneration", { ...form, amount: Number(form.amount) });
    setForm({ fy: "25-26", head: "", amount: "" }); load(); toast.success("Added");
  };
  const del = async (id) => { await api.delete(`/reports/schedule3/auditor-remuneration/${id}`); load(); };
  return (
    <div className="card-sharp p-5" data-testid="aud-disclosure">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Auditor Remuneration</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <Input placeholder="FY (e.g. 25-26)" value={form.fy} onChange={(e) => setForm({ ...form, fy: e.target.value })} data-testid="aud-fy" />
        <Input placeholder="Head (audit, tax, other)" value={form.head} onChange={(e) => setForm({ ...form, head: e.target.value })} data-testid="aud-head" />
        <Input placeholder="Amount ₹" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="aud-amount" />
        <Button onClick={add} data-testid="aud-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b"><th className="text-left py-2">FY</th><th className="text-left">Head</th><th className="text-right">Amount</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="text-muted-foreground text-center py-3">No entries yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b">
              <td className="py-2">{r.fy}</td>
              <td>{r.head}</td>
              <td className="text-right">{fmtINR(r.amount)}</td>
              <td className="text-right"><Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ScheduleIIINotes() {
  const today = new Date().toISOString().slice(0, 10);
  const y = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1;
  const [fromDate, setFromDate] = useState(`${y}-04-01`);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const drill = useContext(DrillCtx);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/schedule3/notes?from_date=${fromDate}&to_date=${toDate}`);
      setData(r.data);
    } catch (e) { toast.error("Failed to load notes"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportXlsx = () =>
    downloadXlsx(`/reports/schedule3/excel?from_date=${fromDate}&to_date=${toDate}`,
                 `Schedule3_Notes_${fromDate}_to_${toDate}.xlsx`);

  if (!data) return <div className="p-6 text-muted-foreground" data-testid="s3-loading">{loading ? "Loading Schedule III Notes…" : "…"}</div>;

  const ar = data.notes.trade_receivables_ageing;
  const ap = data.notes.trade_payables_ageing;

  return (
    <div className="space-y-6" data-testid="s3-notes">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 card-sharp p-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">From</div>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="s3-from" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">To</div>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="s3-to" />
        </div>
        <Button onClick={load} disabled={loading} data-testid="s3-refresh">Refresh</Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="s3-export-xlsx" className="ml-auto">
          <Download className="w-4 h-4 mr-2" /> Export Full Excel (22 sheets)
        </Button>
      </div>

      {/* Ratios grid */}
      <div className="card-sharp p-5" data-testid="s3-ratios">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Ratios · Schedule III Amendment 2021 (11 mandatory)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(RATIO_LABELS).map(([k, label]) => {
            const r = data.ratios[k]; const v = r?.value;
            return (
              <div key={k} className="border border-border p-3" data-testid={`ratio-${k}`}>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-lg font-mono mt-1">{v === null || v === undefined ? "—" : Number(v).toFixed(4)}</div>
                <div className="text-[10px] text-muted-foreground mt-1 truncate" title={r?.formula}>{r?.formula}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AR / AP ageing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-sharp p-5" data-testid="s3-ar-ageing">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Trade Receivables · Ageing</div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(AGE_LABELS).map(([k, l]) => (
                <tr key={k} className="border-b"><td className="py-2">{l}</td><td className="text-right font-mono">{fmtINR(ar.buckets[k])}</td></tr>
              ))}
              <tr><td className="py-2 font-semibold">Total Outstanding</td><td className="text-right font-mono font-semibold">{fmtINR(ar.total_outstanding)}</td></tr>
            </tbody>
          </table>
          {ar.top_10_debtors?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Top 10 Debtors</div>
              <table className="w-full text-sm">
                <tbody>{ar.top_10_debtors.map((p, i) => (<tr key={i} className="border-b"><td className="py-1">{p.party_name}</td><td className="text-right font-mono">{fmtINR(p.amount)}</td></tr>))}</tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-sharp p-5" data-testid="s3-ap-ageing">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Trade Payables · Ageing</div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(AGE_LABELS).map(([k, l]) => (
                <tr key={k} className="border-b"><td className="py-2">{l}</td><td className="text-right font-mono">{fmtINR(ap.buckets[k])}</td></tr>
              ))}
              <tr><td className="py-2 font-semibold">Total Outstanding</td><td className="text-right font-mono font-semibold">{fmtINR(ap.total_outstanding)}</td></tr>
              <tr><td className="py-2 text-orange-500">MSME dues (u/s 22 MSMED Act)</td><td className="text-right font-mono text-orange-500">{fmtINR(ap.msme_outstanding)}</td></tr>
            </tbody>
          </table>
          {ap.top_10_creditors?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Top 10 Creditors</div>
              <table className="w-full text-sm">
                <tbody>{ap.top_10_creditors.map((p, i) => (<tr key={i} className="border-b"><td className="py-1">{p.party_name}</td><td className="text-right font-mono">{fmtINR(p.amount)}</td></tr>))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Top accounts per bucket */}
      <div className="card-sharp p-5" data-testid="s3-top-accounts">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Top Accounts per Schedule III Bucket · <span className="italic normal-case text-muted-foreground">click a row to drill into its ledger</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries({ ...data.notes.top_bs_accounts, ...data.notes.top_pl_accounts }).map(([bucket, rows]) => (
            <div key={bucket} className="border border-border p-3">
              <div className="text-xs font-mono uppercase text-muted-foreground mb-2">{bucket.replace(/_/g, " ")}</div>
              <table className="w-full text-sm">
                <tbody>{rows.slice(0, 5).map((row, i) => (
                  <tr key={i}
                      className={`border-b ${row.account_id ? "cursor-pointer group hover:bg-muted/40" : ""}`}
                      onClick={() => row.account_id && drill && drill.openAccount(row.account_id, `${row.code} · ${row.name}`, { from_date: fromDate, to_date: toDate })}
                      data-testid={`s3-top-row-${bucket}-${i}`}>
                    <td className="py-1 text-muted-foreground w-12">{row.code}</td>
                    <td className="truncate"><span className={row.account_id ? "group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2" : ""}>{row.name}</span></td>
                    <td className="text-right font-mono">{fmtINR(row.amount)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ))}
        </div>
      </div>

      {/* Related Parties */}
      <div className="card-sharp p-5" data-testid="s3-related-parties">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Related Party Transactions (AS-18 / Ind AS 24)</div>
        {data.disclosures.related_party_transactions.length === 0 ? (
          <div className="text-sm text-muted-foreground">Mark a party as related in the Parties page (<code>is_related=true</code>) to see transactions here.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2">Party</th><th className="text-left">Relationship</th><th className="text-right">Sales To</th><th className="text-right">Purchases From</th></tr></thead>
            <tbody>{data.disclosures.related_party_transactions.map((r, i) => (
              <tr key={i} className="border-b"><td className="py-2">{r.party_name}</td><td>{r.relationship}</td><td className="text-right font-mono">{fmtINR(r.sales_to)}</td><td className="text-right font-mono">{fmtINR(r.purchases_from)}</td></tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {/* Contingent + Auditor */}
      <CLDisclosure />
      <AuditorDisclosure />
    </div>
  );
}
